/**
 * LegacyOrRedirect — wrapper used to migrate the old
 *   /sms · /voicemail · /contacts · /communications-log · /healthcare/broadcasts
 * routes into the new consolidated /phone workspace, without breaking
 * anyone's bookmarks.
 *
 * Default: navigates to `target` on mount.
 * Escape hatch: appending `?legacy=1` (or `?embed=1` when the page is
 *   loaded inside an iframe from the new workspace) renders the
 *   pre-existing legacyElement, so someone who's still on the old page
 *   from a bookmark can pin `bookmark?legacy=1` and keep working.
 *   `?embed=1` exists because the new /phone workspace embeds legacy
 *   pages inside iframes; without recognizing it here, the iframe would
 *   recursively navigate back to /phone (iter 449 bug).
 *
 * When the new workspace is trusted, delete this wrapper and change the
 * routes to use plain <Navigate replace to=... />.
 */
import { Navigate, useLocation } from "react-router-dom";

export default function LegacyOrRedirect({ target, legacyElement }) {
  const loc = useLocation();
  const q = new URLSearchParams(loc.search);
  // ?legacy=1 → user bookmark; ?embed=1 → iframe from the new /phone
  // workspace. Either flag renders the legacy element and skips the
  // redirect so the tab-embed doesn't recurse.
  if (q.get("legacy") === "1" || q.get("embed") === "1") return legacyElement;
  // Preserve any additional query params the caller might have set
  // (e.g. /sms?to=+14145551212 → /phone?tab=inbox&filter=texts&to=...).
  const [basePath, baseQuery = ""] = target.split("?");
  const merged = new URLSearchParams(baseQuery);
  q.forEach((v, k) => { if (!merged.has(k) && k !== "legacy" && k !== "embed") merged.set(k, v); });
  const finalTarget = merged.toString() ? `${basePath}?${merged.toString()}` : basePath;
  return <Navigate to={finalTarget} replace />;
}
